/**
 * FLAG:useDocuAi — keeps the drawer's preview pane from collapsing and re-opening while the
 * document it shows is still being refreshed.
 *
 * `hasNoPreview` decides whether the preview pane exists, and the drawer animates that decision:
 * `content` is `510px` without a preview and `100vw` with one, under `transition: width 0.4s`.
 * So every flip of that boolean is not a flicker but a half-second SLIDE of the whole drawer.
 *
 * The flip happens because the drawer replaces its document object several times right after an
 * upload (each FILE:ANALYSED / image-stats / access event dispatches a re-fetch, and the initial
 * seed comes from the list row, not the detail payload). Intermediate payloads legitimately carry
 * `uploadToken`, or do not carry `mimetype` yet — and both answers change `hasNoPreview`.
 *
 * A PDF barely shows it: it survives on the `previewMimeTypes` allowlist. A video has no entry in
 * that allowlist at all, so its pane hangs entirely on the flag-ON clause, and every gap in the
 * payload closes it.
 *
 * Whether a file can be previewed is a property of the FILE, not of the payload that happens to
 * be in flight. So once we have seen that a document has a preview, that verdict holds for as
 * long as the drawer shows that same document; opening a different document decides afresh.
 */

export interface PreviewGateMemory {
  /** The document the remembered verdict belongs to. */
  documentId?: string | number | null
  /** Whether that document was ever seen with a preview. */
  hadPreview: boolean
}

export const initialPreviewGate: PreviewGateMemory = { documentId: undefined, hadPreview: false }

/**
 * Pure state transition — the caller keeps `memory` (a ref) and uses the returned `hasNoPreview`.
 *
 * Deliberately one-way: it can only ever turn "no preview" INTO "preview", never the reverse.
 * That is the whole point, and it is safe because the only way to lose a preview for real is to
 * open a different document, which resets the memory.
 */
export const nextPreviewGate = (
  memory: PreviewGateMemory,
  documentId: string | number | null | undefined,
  hasNoPreviewNow: boolean,
): { memory: PreviewGateMemory, hasNoPreview: boolean } => {
  // A different document (or the first one): decide fresh, remember the verdict.
  if (!memory || memory.documentId !== documentId) {
    return {
      memory: { documentId, hadPreview: !hasNoPreviewNow },
      hasNoPreview: hasNoPreviewNow,
    }
  }

  // Same document, already known to have a preview: hold the pane open through the gap.
  if (memory.hadPreview) {
    return { memory, hasNoPreview: false }
  }

  // Same document, no preview seen yet: this payload may be the one that reveals it.
  return {
    memory: { documentId, hadPreview: !hasNoPreviewNow },
    hasNoPreview: hasNoPreviewNow,
  }
}
